import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { LEGACY_SOURCE } from '@/lib/sourceChannelTypes';

export interface SourceBreakdownRow {
  source: string;
  weightPct: number | null;
  goalAmount: number | null;
  goalDeals: number | null;
  actualAmount: number;
  actualCount: number;
  pctAchieved: number | null;
}

function normalizeSource(source: string | null | undefined): string {
  if (!source || source === LEGACY_SOURCE || source === '') return 'Other';
  return source;
}

export function useConsultantSourceBreakdown(
  consultantId: string | null,
  year: number,
  month: number
) {
  return useQuery({
    queryKey: ['consultant-source-breakdown', consultantId, year, month],
    enabled: !!consultantId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!consultantId) return [];

      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endMonth = month === 12 ? 1 : month + 1;
      const endYear = month === 12 ? year + 1 : year;
      const endDate = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;

      // 1. Get consultant's individual goals (to find their BU and total goal)
      const goalRes = await supabase
        .from('user_sales_goals')
        .select('goal_amount, business_unit')
        .eq('user_id', consultantId)
        .eq('year', year)
        .eq('month', month)
        .not('business_unit', 'is', null);

      const goalRows = goalRes.data ?? [];
      const totalGoal = goalRows.reduce((sum, g) => sum + (Number(g.goal_amount) || 0), 0);

      // Determine consultant's BU (use first non-null BU found)
      const consultantBU = goalRows.length > 0 ? goalRows[0].business_unit : null;

      // Parallel: global weights (filtered by BU), deals, avg_ticket (filtered by BU)
      const queries: Promise<any>[] = [
        // Global source weights filtered by consultant's BU
        consultantBU
          ? (supabase as any)
              .from('sales_goals_source_weights')
              .select('source, weight_pct')
              .eq('year', year)
              .eq('month', month)
              .eq('business_unit', consultantBU)
          : (supabase as any)
              .from('sales_goals_source_weights')
              .select('source, weight_pct')
              .eq('year', year)
              .eq('month', month),

        // Won deals
        supabase
          .from('deals')
          .select('deals_source, total_amount, upsell_credit_amount, deal_status, cancelled_at, closed_date')
          .eq('sales_consultant', consultantId)
          .eq('stage', 'won')
          .gte('closed_date', startDate)
          .lt('closed_date', endDate),

        // Avg ticket filtered by consultant's BU
        consultantBU
          ? (supabase as any)
              .from('sales_goals')
              .select('avg_ticket')
              .eq('year', year)
              .eq('month', month)
              .eq('business_unit', consultantBU)
          : (supabase as any)
              .from('sales_goals')
              .select('avg_ticket')
              .eq('year', year)
              .eq('month', month),
      ];

      const [globalWeightsRes, dealsRes, avgTicketRes] = await Promise.all(queries);

      // Avg ticket (BU-specific)
      const avgTicketRows = avgTicketRes.data ?? [];
      const avgTicket = avgTicketRows.length > 0
        ? avgTicketRows.reduce((sum: number, r: any) => sum + (Number(r.avg_ticket) || 0), 0) / avgTicketRows.length
        : 0;

      // Always use global weights (same as admin)
      const weightMap = new Map<string, number>();
      (globalWeightsRes.data ?? []).forEach((w: any) => {
        const pct = Number(w.weight_pct) || 0;
        const existing = weightMap.get(w.source) ?? 0;
        if (pct > existing) weightMap.set(w.source, pct);
      });

      // Actuals from deals
      const actualsMap = new Map<string, { revenue: number; count: number }>();
      (dealsRes.data ?? []).forEach((deal: any) => {
        if (deal.deal_status === 'cancelled' && deal.cancelled_at && deal.closed_date) {
          const closedYM = deal.closed_date.substring(0, 7);
          const cancelledYM = deal.cancelled_at.substring(0, 7);
          if (closedYM === cancelledYM) return;
        }
        const source = normalizeSource(deal.deals_source);
        const revenue = (Number(deal.total_amount) || 0) - (Number(deal.upsell_credit_amount) || 0);
        const existing = actualsMap.get(source) || { revenue: 0, count: 0 };
        existing.revenue += revenue;
        existing.count += 1;
        actualsMap.set(source, existing);
      });

      // Merge sources
      const allSources = new Set([...weightMap.keys(), ...actualsMap.keys()]);
      const rows: SourceBreakdownRow[] = [];

      allSources.forEach(source => {
        const wPct = weightMap.get(source) ?? null;
        const goalAmt = wPct !== null && totalGoal > 0 ? totalGoal * (wPct / 100) : null;
        const gDeals = avgTicket > 0 && goalAmt !== null && goalAmt > 0
          ? Math.round(goalAmt / avgTicket)
          : null;
        const actuals = actualsMap.get(source) || { revenue: 0, count: 0 };
        const pct = goalAmt !== null && goalAmt > 0 ? (actuals.revenue / goalAmt) * 100 : null;

        rows.push({
          source,
          weightPct: wPct,
          goalAmount: goalAmt,
          goalDeals: gDeals && gDeals > 0 ? gDeals : null,
          actualAmount: actuals.revenue,
          actualCount: actuals.count,
          pctAchieved: pct,
        });
      });

      rows.sort((a, b) => {
        if (a.weightPct !== null && b.weightPct === null) return -1;
        if (a.weightPct === null && b.weightPct !== null) return 1;
        if (a.weightPct !== null && b.weightPct !== null) return b.weightPct - a.weightPct;
        return b.actualAmount - a.actualAmount;
      });

      return rows;
    },
  });
}
